import { useState, useEffect, useMemo } from 'react';
import { Calendar, ChevronDown, Package, RefreshCw } from 'lucide-react';
import { DatePicker } from '~/components/ui/DatePicker';
import { format } from 'date-fns';
import {
  deriveScheduledOccurrenceCount,
  getMinimumScheduledDate,
  isScheduledDateOnOrAfterMinimum,
} from '~/utils/scheduled-order';

type Frequency = 'daily' | 'weekly' | 'custom';

export interface DeliverySchedule {
  frequency: Frequency;
  startDate: string;
  endDate: string;
  selectedDays: string[];
  customInterval: number;
  orderCount: number;
  isRepeatOrder: boolean;
  singleDeliveryDate?: string;
}

interface AdvancedOrderPanelProps {
  frequency: Frequency;
  onFrequencyChange: (freq: Frequency) => void;
  onOrderCountChange: (count: number) => void;
  onScheduleChange: (schedule: DeliverySchedule) => void;
  initialSchedule?: DeliverySchedule;
}

export function AdvancedOrderPanel({
  frequency,
  onFrequencyChange,
  onOrderCountChange,
  onScheduleChange,
  initialSchedule,
}: AdvancedOrderPanelProps) {
  const minimumDeliveryDate = getMinimumScheduledDate();

  // Track if we've loaded initialSchedule (to avoid emitting default state before loading)
  const [isInitialized, setIsInitialized] = useState(!initialSchedule);

  // Initialize state from initialSchedule if provided
  useEffect(() => {
    if (initialSchedule) {
      setIsRepeatOrder(initialSchedule.isRepeatOrder);
      if (initialSchedule.singleDeliveryDate) {
        setSingleDeliveryDate(initialSchedule.singleDeliveryDate);
      }
      if (initialSchedule.startDate) {
        setStartDate(initialSchedule.startDate);
      }
      if (initialSchedule.endDate) {
        setEndDate(initialSchedule.endDate);
      }
      if (
        initialSchedule.selectedDays &&
        initialSchedule.selectedDays.length > 0
      ) {
        setSelectedDays(initialSchedule.selectedDays);
      }
      if (initialSchedule.customInterval) {
        setCustomValue(initialSchedule.customInterval);
      }
      // Mark as initialized AFTER setting all state
      setIsInitialized(true);
    }
  }, [initialSchedule]);

  // Repeat Order toggle - default OFF (single delivery date mode)
  const [isRepeatOrder, setIsRepeatOrder] = useState(
    initialSchedule?.isRepeatOrder ?? false,
  );

  // Single delivery date (for non-repeat orders)
  const [singleDeliveryDate, setSingleDeliveryDate] = useState(
    initialSchedule?.singleDeliveryDate ?? minimumDeliveryDate,
  );

  // Repeat order fields
  const [startDate, setStartDate] = useState(
    initialSchedule?.startDate ?? minimumDeliveryDate,
  );
  const [endDate, setEndDate] = useState(
    initialSchedule?.endDate ?? minimumDeliveryDate,
  );
  const [selectedDays, setSelectedDays] = useState<string[]>(
    initialSchedule?.selectedDays ?? [],
  );
  const [customValue, setCustomValue] = useState(
    initialSchedule?.customInterval ?? 3,
  );
  const singleDeliveryDateTooEarly =
    !isRepeatOrder &&
    Boolean(singleDeliveryDate) &&
    !isScheduledDateOnOrAfterMinimum(singleDeliveryDate);
  const startDateTooEarly =
    isRepeatOrder &&
    Boolean(startDate) &&
    !isScheduledDateOnOrAfterMinimum(startDate);
  const minimumDateError = `Scheduled delivery must be ${minimumDeliveryDate} or later.`;

  const toggleDay = (day: string) => {
    if (selectedDays.includes(day)) {
      setSelectedDays(selectedDays.filter((d) => d !== day));
    } else {
      setSelectedDays([...selectedDays, day]);
    }
  };

  const days = [
    { label: 'S', value: 'Sun', dayIndex: 0 },
    { label: 'M', value: 'Mon', dayIndex: 1 },
    { label: 'T', value: 'Tue', dayIndex: 2 },
    { label: 'W', value: 'Wed', dayIndex: 3 },
    { label: 'Th', value: 'Thu', dayIndex: 4 },
    { label: 'F', value: 'Fri', dayIndex: 5 },
    { label: 'Sa', value: 'Sat', dayIndex: 6 },
  ];

  const customIntervalOptions = [2, 3, 4, 5, 6, 8, 10];

  // Calculate the count with the same inclusive recurrence rules as the server.
  const orderCount = useMemo(() => {
    return (
      deriveScheduledOccurrenceCount({
        itemType: isRepeatOrder ? 'recurring' : 'future',
        frequency: isRepeatOrder ? frequency : 'daily',
        intervalStep: isRepeatOrder ? customValue : 0,
        startDate: isRepeatOrder ? startDate : singleDeliveryDate,
        endDate: isRepeatOrder ? endDate : singleDeliveryDate,
        daysOfWeek: isRepeatOrder && frequency === 'weekly' ? selectedDays : [],
      }) ?? 0
    );
  }, [
    isRepeatOrder,
    singleDeliveryDate,
    startDate,
    endDate,
    frequency,
    selectedDays,
    customValue,
  ]);

  // Notify parent of order count and schedule changes
  useEffect(() => {
    // Don't emit until we've loaded initialSchedule (if one was provided)
    if (!isInitialized) return;

    onOrderCountChange(orderCount);
    onScheduleChange({
      frequency: isRepeatOrder ? frequency : 'daily',
      startDate: isRepeatOrder ? startDate : singleDeliveryDate,
      endDate: isRepeatOrder ? endDate : singleDeliveryDate,
      selectedDays: isRepeatOrder && frequency === 'weekly' ? selectedDays : [],
      customInterval: isRepeatOrder && frequency === 'custom' ? customValue : 0,
      orderCount,
      isRepeatOrder,
      singleDeliveryDate: !isRepeatOrder ? singleDeliveryDate : undefined,
    });
  }, [
    isInitialized,
    orderCount,
    isRepeatOrder,
    singleDeliveryDate,
    frequency,
    startDate,
    endDate,
    selectedDays,
    customValue,
    onOrderCountChange,
    onScheduleChange,
  ]);

  return (
    <div className="space-y-5">
      {/* Repeat Order Checkbox */}
      <label className="flex items-center gap-3 cursor-pointer group">
        <div
          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
            isRepeatOrder
              ? 'bg-[#E80245] border-[#E80245]'
              : 'border-gray-300 group-hover:border-[#E80245]'
          }`}
        >
          {isRepeatOrder && (
            <svg
              className="w-3 h-3 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={3}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          )}
        </div>
        <input
          type="checkbox"
          checked={isRepeatOrder}
          onChange={(e) => setIsRepeatOrder(e.target.checked)}
          className="hidden"
        />
        <div className="flex items-center gap-2">
          <RefreshCw
            className={`w-4 h-4 ${
              isRepeatOrder ? 'text-[#E80245]' : 'text-gray-400'
            }`}
          />
          <span
            className={`text-sm font-medium ${
              isRepeatOrder ? 'text-gray-900' : 'text-gray-600'
            }`}
          >
            Repeat Order?
          </span>
        </div>
      </label>

      {/* Single Delivery Date Mode (when NOT repeat) */}
      {!isRepeatOrder && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Delivery Date:
          </label>
          <DatePicker
            selected={singleDeliveryDate}
            onSelect={(date) => {
              if (date) setSingleDeliveryDate(format(date, 'yyyy-MM-dd'));
            }}
            minDate={new Date(`${minimumDeliveryDate}T00:00:00`)}
            isInvalid={singleDeliveryDateTooEarly}
            errorMessage={
              singleDeliveryDateTooEarly ? minimumDateError : undefined
            }
          />
          <p className="text-xs text-gray-500">
            Your order will be delivered on this date.
          </p>
        </div>
      )}

      {/* Repeat Order Mode */}
      {isRepeatOrder && (
        <>
          {/* Start/End Date Selector */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Start Date:
              </label>
              <DatePicker
                selected={startDate}
                onSelect={(date) => {
                  if (date) setStartDate(format(date, 'yyyy-MM-dd'));
                }}
                minDate={new Date(`${minimumDeliveryDate}T00:00:00`)}
                isInvalid={startDateTooEarly}
                errorMessage={startDateTooEarly ? minimumDateError : undefined}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                End Date:
              </label>
              <DatePicker
                selected={endDate}
                onSelect={(date) => {
                  if (date) setEndDate(format(date, 'yyyy-MM-dd'));
                }}
                minDate={
                  startDate
                    ? new Date(`${startDate}T00:00:00`)
                    : new Date(`${minimumDeliveryDate}T00:00:00`)
                }
                isInvalid={
                  !!(
                    startDate &&
                    endDate &&
                    new Date(endDate) < new Date(startDate)
                  )
                }
                errorMessage={
                  !!(
                    startDate &&
                    endDate &&
                    new Date(endDate) < new Date(startDate)
                  )
                    ? 'End date must be after start date.'
                    : undefined
                }
                align="end"
              />
            </div>
          </div>

          {/* Frequency Selector */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700">
              Select frequencies
            </label>
            <div className="flex items-center gap-6">
              {(['daily', 'weekly', 'custom'] as const).map((freq) => (
                <label
                  key={freq}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                      frequency === freq
                        ? 'border-[#E80245]'
                        : 'border-gray-300'
                    }`}
                  >
                    {frequency === freq && (
                      <div className="w-2.5 h-2.5 rounded-full bg-[#E80245]" />
                    )}
                  </div>
                  <input
                    type="radio"
                    name="frequency"
                    value={freq}
                    checked={frequency === freq}
                    onChange={() => onFrequencyChange(freq)}
                    className="hidden"
                  />
                  <span
                    className={`text-sm ${
                      frequency === freq
                        ? 'font-medium text-gray-900'
                        : 'text-gray-600'
                    }`}
                  >
                    {freq === 'daily' && 'Daily'}
                    {freq === 'weekly' && 'Weekly'}
                    {freq === 'custom' && 'Custom Interval'}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Weekly - Day Picker */}
          {frequency === 'weekly' && (
            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-700">
                Choose Delivery Day
              </label>
              <div className="flex gap-2">
                {days.map((day) => {
                  const isSelected = selectedDays.includes(day.value);
                  return (
                    <button
                      key={day.value}
                      type="button"
                      onClick={() => toggleDay(day.value)}
                      className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-medium transition-all ${
                        isSelected
                          ? 'bg-[#E80245] text-white shadow-md'
                          : 'bg-white border border-gray-200 text-gray-500 hover:border-[#E80245]'
                      }`}
                    >
                      {day.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Custom Interval */}
          {frequency === 'custom' && (
            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-700">
                Number of days between each order.
              </label>
              <div className="flex items-center gap-2">
                {customIntervalOptions.map((val) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setCustomValue(val)}
                    className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                      customValue === val
                        ? 'bg-[#E80245] text-white shadow-md'
                        : 'bg-white border border-gray-200 text-gray-500 hover:border-[#E80245]'
                    }`}
                  >
                    {val}
                  </button>
                ))}
                {/* Dropdown for more options */}
                <button className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:border-gray-400 transition-colors">
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Order Count Preview / Validation Error */}
      {isRepeatOrder && orderCount === 0 ? (
        <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg border border-red-200 text-red-600">
          <span className="text-sm font-medium">
            No deliveries scheduled. Please select a valid date range and
            delivery day(s).
          </span>
        </div>
      ) : (
        orderCount > 0 && (
          <div className="flex items-center gap-2 p-3 bg-[#FFF0F3] rounded-lg border border-[#E80245]/20">
            <Package className="w-4 h-4 text-[#E80245]" />
            <span className="text-sm font-medium text-gray-700">
              <span className="text-[#E80245] font-bold">{orderCount}</span>{' '}
              {orderCount === 1 ? 'order' : 'orders'} will be placed
            </span>
          </div>
        )
      )}
    </div>
  );
}
